<template>
  <div class="comment">
    <Avatar
      :user="comment.author"
      class="user_avatar"
      @click="openProfile(comment.author?.id)"
    />
    <div class="comment_box">
      <div class="comment_info">
        <div class="comment_text_info">
          <a class="comment_username" @click="openProfile(comment.author?.id)">
            {{ comment.author?.username || "" }}
          </a>
          <div class="comment_date">{{ commentDate }}</div>
        </div>

        <button
          v-if="auth.user"
          class="comment_active_btn"
          @click.stop="viewHints = !viewHints"
        >
          <img :src="currentImage.pointers" />
        </button>

        <div class="hints" v-show="viewHints">
          <div class="hints_btn" @click="replyComment">
            <img :src="currentImage.answer" />
            {{ $t("comments.active.answer") }}
          </div>
          <div
            v-if="auth.user.id !== comment.author.id"
            class="hints_btn"
            @click.stop="openReportModal"
          >
            <img :src="currentImage.report" />
            {{ $t("comments.active.report") }}
          </div>
          <div v-else class="hints_btn" @click.stop="deleteComment">
            <img :src="currentImage.remove" />
            {{ $t("comments.active.remove") }}
          </div>
        </div>
      </div>
      <div class="comment_text">
        <template v-for="(chunk, i) in parsedComment" :key="i">
          <span v-if="chunk.type === 'text'">
            {{ chunk.value }}
          </span>

          <span v-else class="mention" @click="openUser(chunk.username)">
            @{{ chunk.username }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/Avatar.vue";
import { useModalStore } from "@/stores/modalStore";
import { formatDistanceToNow, differenceInMinutes } from "date-fns";
import { ru, enUS } from "date-fns/locale";
import { useThemeStore } from "@/stores/themeStore";
import { searchUserByUsername } from "@/api/axios.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useClickOutside } from "@/composables/useClickOutside.js";
import Report from "./Report.vue";
import ConfirmDeletion from "./ConfirmDeletion.vue";
import { useOpenProfile } from "@/composables/useOpenProfile.js";

export default {
  name: "Comment",
  components: { Avatar, Report },
  props: {
    comment: {
      type: Object,
    },
  },
  data() {
    return {
      now: new Date(),
      modal: useModalStore(),
      viewHints: false,
      currentImage: {},
      themeStore: useThemeStore(),
      auth: useAuthStore(),
      removeClickOutside: null,
      openProfile: useOpenProfile().openProfile,
    };
  },
  methods: {
    replyComment() {
      this.$emit(
        "setCommentText",
        "@" + this.comment?.author.username + " " + this.$parent.commentText,
      );
      this.viewHints = false;
    },
    async openUser(username) {
      try {
        const res = await searchUserByUsername(username);
        const user = res.data.results[0];

        this.modal.closeAllModals();
        this.$router.push({
          name: "UserProfile",
          params: { id: user.id },
        });
      } catch (e) {
        console.log("Error serach user by username.");
      }
    },
    openReportModal() {
      this.modal.openModal({
        component: Report,
        props: {
          comment: this.comment,
        },
        options: { lockScroll: true },
      });
    },
    deleteComment() {
      this.modal.openModal({
        component: ConfirmDeletion,
        props: {
          commentId: this.comment.id,
          onDeleted: (id) => this.$emit("deleted", id),
        },
        options: { lockScroll: true },
      });
    },
    updateImage() {
      this.currentImage.report = this.themeStore.isDark
        ? new URL("/Report.svg", import.meta.url).href
        : new URL("/Report-dark.svg", import.meta.url).href;

      this.currentImage.answer = this.themeStore.isDark
        ? new URL("/share-icon.svg", import.meta.url).href
        : new URL("/share-icon-dark.svg", import.meta.url).href;

      this.currentImage.pointers = this.themeStore.isDark
        ? new URL("/pointers.svg", import.meta.url).href
        : new URL("/pointers-dark.svg", import.meta.url).href;

      this.currentImage.remove = this.themeStore.isDark
        ? new URL("/trash.svg", import.meta.url).href
        : new URL("/trash-dark.svg", import.meta.url).href;
    },
  },
  computed: {
    commentDate() {
      const dateStr = this.comment?.time_created;
      if (!dateStr) return "";

      const dateUTC = new Date(dateStr); // UTC
      const dateLocal = new Date(
        dateUTC.getTime() + dateUTC.getTimezoneOffset() * 60000,
      );

      if (differenceInMinutes(this.now, dateLocal) < 1) {
        return this.$t("justNow");
      }

      return formatDistanceToNow(dateLocal, {
        locale: this.$i18n.locale === "ru" ? ru : enUS,
        addSuffix: true,
      });
    },
    parsedComment() {
      if (!this.comment?.text) return [];

      return this.comment.text.split(/(@\w+)/g).map((part) => {
        if (part.startsWith("@")) {
          return {
            type: "mention",
            username: part.slice(1),
          };
        }
        return {
          type: "text",
          value: part,
        };
      });
    },
  },
  watch: {
    "themeStore.isDark": function () {
      this.updateImage();
    },
  },
  mounted() {
    this.updateImage();
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 60_000);

    this.removeClickOutside = useClickOutside(
      this.$el,
      () => (this.viewHints = false),
    );
  },
  beforeUnmount() {
    this.removeClickOutside?.();
    clearInterval(this.timer);
  },
};
</script>

<style>
.comment {
  display: flex;
  gap: 4px;
  padding-bottom: 13px;
  color: var(--color-picker);
  padding: 8px 12px;
  box-sizing: border-box;
  width: 100%;
  position: relative;
  overflow: visible;
}

.user_avatar {
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
}

.user_avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.comment_box {
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.comment_info {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.comment_text_info {
  display: flex;
  gap: 12px;
  font-size: 13px;
}

.comment_text {
  color: var(--text-color);
  font-size: 14px;
}

.comment_active_btn {
  border-radius: 12px;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
}

.comment_active_btn:hover {
  opacity: 50%;
}

.comment_username {
  cursor: pointer;
  font-weight: 700;
}

.mention {
  color: rgba(255, 204, 0, 1);
  cursor: pointer;
  font-weight: 700;
}

.hints {
  position: absolute;
  top: 25px;
  right: -15px;
  background: var(--color-picker-border);
  box-shadow: 0px 0px 7px 0px rgba(0, 0, 0, 0.25);
  border-radius: 10px;
  box-sizing: border-box;
  z-index: 2;
}

.hints::after {
  content: "";
  position: absolute;
  transform: rotate(180deg);
  right: 15px;
  top: -20px;
  border: 10px solid transparent;
  border-top: 10px solid var(--color-picker-border);
}

.hints_btn {
  display: flex;
  color: var(--text-color);
  gap: 17px;
  padding: 17px;
  cursor: pointer;
  font-weight: 400;
  font-size: 14px;
  border-radius: 10px;
  align-items: center;
}

.hints_btn img {
  width: 18px;
  height: 18px;
}

.hints_btn:hover {
  background: var(--hover);
}
</style>
